/**
 * The Business Builder Accelerator for the signed-in coach: what she is
 * offered, its checkout, and the purchase check on her return from Checkout.
 * Thin wrappers around `~/lib/server/accelerator`.
 *
 * Aliases: accelerator api, accelerator functions, accelerator call link
 */

import { createServerFn } from "@tanstack/react-start";
import { getRequestUrl } from "@tanstack/react-start/server";
import { z } from "zod";

import { requireClerkCoachAuth } from "~/lib/coach-middleware";
import type { AcceleratorView } from "~/lib/shared/accelerator-offer";
import { RETURN_PATH_MAX_LENGTH } from "~/lib/shared/return-path";
import { getAppUrl } from "~/lib/utils";

/**
 * What the course card, the band in My assets and the offer page show her.
 * The group call's join link is in it only for a coach in the Accelerator,
 * checked on the server from her coach row.
 */
export const getMyAccelerator = createServerFn({ method: "GET" })
  .middleware([requireClerkCoachAuth])
  .handler(async ({ context }): Promise<AcceleratorView> => {
    const { readAcceleratorView } =
      await import("~/lib/server/accelerator/offer");
    return readAcceleratorView(context.coach);
  });

export const createAcceleratorCheckoutSession = createServerFn({
  method: "POST",
})
  .validator(
    z.object({
      /** The price the offer showed. A reprice since then is refused. */
      quotedPriceId: z.string().min(1).max(255),
      /** The screen to come back to. The server checks it. */
      returnTo: z.string().max(RETURN_PATH_MAX_LENGTH).optional(),
    }),
  )
  .middleware([requireClerkCoachAuth])
  .handler(async ({ context, data }): Promise<{ url: string }> => {
    const { createAcceleratorCheckoutUrl } =
      await import("~/lib/server/accelerator/checkout");
    const appUrl = getAppUrl({ url: getRequestUrl().href } as Request);
    const url = await createAcceleratorCheckoutUrl({
      coach: context.coach,
      appUrl,
      returnTo: data.returnTo,
      quotedPriceId: data.quotedPriceId,
    });
    return { url };
  });

/**
 * Records the purchase on her return from Checkout, so the page she lands on
 * already says "You're in" when the webhook has not arrived yet.
 */
export const confirmAcceleratorPurchase = createServerFn({ method: "POST" })
  .validator(z.object({ checkoutSessionId: z.string().min(1).max(255) }))
  .middleware([requireClerkCoachAuth])
  .handler(async ({ context, data }): Promise<{ recorded: boolean }> => {
    const { confirmAcceleratorCheckout } =
      await import("~/lib/server/accelerator/purchase");
    const outcome = await confirmAcceleratorCheckout(
      context.coach,
      data.checkoutSessionId,
    );
    return {
      recorded: outcome === "recorded" || outcome === "already_recorded",
    };
  });
